import * as React from 'react';
import { CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../lib/utils';
import { formatMonthVi, isYmInRange, parseYm } from '../lib/dateStrings';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

export type MonthPickerProps = {
  /** 'YYYY-MM' — same contract as `<input type="month">`. */
  value: string;
  onChange: (value: string) => void;
  /** 'YYYY-MM' inclusive bounds — out-of-range months are disabled in the grid. */
  min?: string;
  max?: string;
  disabled?: boolean;
  id?: string;
  'aria-label'?: string;
  className?: string;
  placeholder?: string;
  formatValue?: (ym: string) => string;
  monthLabel?: (month: number) => string;
  prevYearLabel?: string;
  nextYearLabel?: string;
};

const DEFAULT_MONTH_LABEL = (month: number) => `Thg ${month}`;

/** Vietnamese-locale month picker (year nav + 3x4 month grid) with the same
 *  string contract as a native `<input type="month">`. See
 *  `apps/dashboard/src/components/pickers/MonthPicker` for the vi-bound
 *  wrapper call sites should use. */
export function MonthPicker({
  value,
  onChange,
  min,
  max,
  disabled,
  id,
  'aria-label': ariaLabel,
  className,
  placeholder = 'Select month',
  formatValue,
  monthLabel = DEFAULT_MONTH_LABEL,
  prevYearLabel = 'Previous year',
  nextYearLabel = 'Next year',
}: MonthPickerProps) {
  const [open, setOpen] = React.useState(false);
  const parsedValue = parseYm(value);
  const [year, setYear] = React.useState<number>(() => parsedValue?.getFullYear() ?? new Date().getFullYear());

  // Re-anchor the visible year to the current value each time the popover opens.
  React.useEffect(() => {
    if (open) {
      setYear(parsedValue?.getFullYear() ?? new Date().getFullYear());
    }
    // Only on open — re-running while the grid is open would fight the user's
    // own prev/next-year navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const display = parsedValue ? (formatValue ? formatValue(value) : formatMonthVi(value)) : null;
  const selectedMonth = parsedValue ? parsedValue.getMonth() : -1;
  const selectedYear = parsedValue ? parsedValue.getFullYear() : -1;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          id={id}
          aria-label={ariaLabel}
          aria-haspopup="dialog"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'h-[28.8px] w-auto justify-start gap-2 text-left font-normal',
            !display && 'text-muted-foreground',
            className,
          )}
        >
          <CalendarIcon className="h-4 w-4 shrink-0" />
          {display ?? placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-3" align="start">
        <div className="flex items-center justify-between gap-2 pb-2">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            aria-label={prevYearLabel}
            onClick={() => setYear((y) => y - 1)}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm font-medium tabular-nums">{year}</span>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            aria-label={nextYearLabel}
            onClick={() => setYear((y) => y + 1)}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <div className="grid grid-cols-3 gap-1.5">
          {Array.from({ length: 12 }, (_, i) => {
            const month = i + 1;
            const candidate = `${year}-${String(month).padStart(2, '0')}`;
            const isSelected = year === selectedYear && i === selectedMonth;
            const isDisabled = !isYmInRange(candidate, min, max);
            return (
              <Button
                key={month}
                type="button"
                variant="ghost"
                aria-pressed={isSelected}
                disabled={isDisabled}
                className={cn(
                  'h-9',
                  isSelected && 'bg-primary text-primary-foreground hover:bg-primary/90',
                )}
                onClick={() => {
                  onChange(candidate);
                  setOpen(false);
                }}
              >
                {monthLabel(month)}
              </Button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
